// The live draft room's socket, as data (REALTIME.md, the A11 decision).
//
// EVERY PAYLOAD IS A VIEW SHAPE. The snapshot is `DraftView`, the same shape
// `GET /api/leagues/:id/draft` serves, and every event carries the NEW VALUE
// of each `DraftView` key it changes, so applying one is assignment and a
// client with no engine can render the room. These are the schemas both ends
// parse, and the contract artifact a client in another language reads.
//
// SERVER MESSAGES CARRY `seq` AND `at`; CLIENT MESSAGES DO NOT. `seq` is the
// room's own counter and `at` the room's clock — a client has neither to give.
// A message sent to one socket only (the snapshot, a refusal, a pong) carries
// the room's current `seq` without advancing it, so it never reads as a gap.

import { z } from 'zod';
import { ApiErrorDetails, ErrorCode } from './envelope';
import {
  DraftAuction,
  DraftBoardCell,
  DraftPlayerRow,
  DraftSeat,
  DraftTrading,
  DraftView,
  PlayerSummary,
} from './views';

const Iso = z.string();
const IntentId = z.string().min(1).max(64);

/**
 * The per-viewer half of the room (REALTIME.md "what the room must derive
 * differently"). Sent to every socket on a pick, because all five change when
 * somebody else drafts a player you had queued. `queuedIds` and
 * `fillsNeedIds` are the flags a shared `available` row cannot carry.
 */
export const DraftMine = z.object({
  myRoster: DraftView.shape.myRoster,
  myQueue: z.array(PlayerSummary),
  myNeeds: z.array(z.string()),
  queuedIds: z.array(z.string()),
  fillsNeedIds: z.array(z.string()),
});
export type DraftMine = z.infer<typeof DraftMine>;

/**
 * One thing a trade offer names: a pick not yet made, by its round and the
 * team whose slot it is, or a player drafted in this draft.
 */
export const DraftTradeItemRef = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('pick'),
    round: z.number().int().min(1).max(99),
    teamId: z.string().min(1),
  }),
  z.object({ kind: z.literal('player'), playerId: z.string().min(1) }),
]);
export type DraftTradeItemRef = z.infer<typeof DraftTradeItemRef>;

/** How an offer stopped waiting, said to its two teams. */
export const DraftOfferClosed = z.object({
  offerId: z.string(),
  fromTeamId: z.string(),
  toTeamId: z.string(),
  /** `void`: a pick in it was made, or something in it changed hands or ended with the draft. */
  reason: z.enum(['accepted', 'declined', 'withdrawn', 'countered', 'void']),
});
export type DraftOfferClosed = z.infer<typeof DraftOfferClosed>;

// ── Client to server ──────────────────────────────────────────────────────

/**
 * What a client may send. The team is never named except where a commissioner
 * acts on another seat: the session says whose team a pick is for.
 */
export const DraftClientMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('draft.start'), payload: z.object({ intentId: IntentId }) }),
  z.object({
    type: z.literal('draft.pick'),
    payload: z.object({
      intentId: IntentId,
      playerId: z.string().min(1),
      /**
       * The team on the clock, when the commissioner is making its pick for
       * it, as Sleeper and ESPN let a commissioner do. Absent is your own.
       */
      teamId: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('draft.queue.set'),
    payload: z.object({ intentId: IntentId, playerIds: z.array(z.string()).max(500) }),
  }),
  z.object({ type: z.literal('draft.pause'), payload: z.object({ intentId: IntentId }) }),
  z.object({ type: z.literal('draft.resume'), payload: z.object({ intentId: IntentId }) }),
  z.object({ type: z.literal('draft.undo'), payload: z.object({ intentId: IntentId }) }),
  z.object({
    type: z.literal('draft.setClock'),
    payload: z.object({
      intentId: IntentId,
      seconds: z
        .number()
        .int()
        .min(15)
        .max(24 * 3600),
    }),
  }),
  z.object({
    type: z.literal('draft.autopilot.set'),
    payload: z.object({
      intentId: IntentId,
      on: z.boolean(),
      /** Another seat's, which only a commissioner may set. Absent is your own. */
      teamId: z.string().optional(),
    }),
  }),
  /**
   * A league mock's lobby (MOCK_DRAFT.md §6.2): take this seat, or give up
   * yours. Only a mock room takes them; the league's own seats are its teams'.
   */
  z.object({
    type: z.literal('draft.seat.take'),
    payload: z.object({ intentId: IntentId, teamId: z.string().min(1) }),
  }),
  z.object({ type: z.literal('draft.seat.leave'), payload: z.object({ intentId: IntentId }) }),
  /**
   * Trading during the draft (DRAFT_ROOM.md "Trading during the draft"): an
   * offer from my seat to another a person holds, the same number each side.
   */
  z.object({
    type: z.literal('draft.trade.offer'),
    payload: z.object({
      intentId: IntentId,
      toTeamId: z.string().min(1),
      /** From me to them. */
      give: z.array(DraftTradeItemRef).max(60),
      /** From them to me. */
      get: z.array(DraftTradeItemRef).max(60),
      /** An offer made to me that this one counters, which ends it. */
      replaces: z.string().min(1).max(64).optional(),
    }),
  }),
  z.object({
    type: z.literal('draft.trade.answer'),
    payload: z.object({ intentId: IntentId, offerId: z.string().min(1), accept: z.boolean() }),
  }),
  z.object({
    type: z.literal('draft.trade.withdraw'),
    payload: z.object({ intentId: IntentId, offerId: z.string().min(1) }),
  }),
  /** The commissioner's: the last trade, taken back while none of its picks has been made. */
  z.object({ type: z.literal('draft.trade.undo'), payload: z.object({ intentId: IntentId }) }),
  /**
   * An auction's (AUCTION.md §3): put a player up at an opening bid — the
   * commissioner may for the team nominating (`teamId`) — bid on one up, or
   * set the most to bid to for you, null to take it off.
   */
  z.object({
    type: z.literal('draft.nominate'),
    payload: z.object({
      intentId: IntentId,
      playerId: z.string().min(1),
      bid: z.number().int().min(0),
      teamId: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('draft.bid'),
    payload: z.object({ intentId: IntentId, lotId: z.number().int(), amount: z.number().int() }),
  }),
  z.object({
    type: z.literal('draft.bid.auto'),
    payload: z.object({
      intentId: IntentId,
      lotId: z.number().int(),
      max: z.number().int().nullable(),
    }),
  }),
  z.object({ type: z.literal('resync'), payload: z.object({ since: z.number().int().min(0) }) }),
  /** For the clock offset (REALTIME.md "Clocks"): the server answers `pong` with its own time. */
  z.object({ type: z.literal('ping'), payload: z.object({ sentAt: z.number() }) }),
]);
export type DraftClientMessage = z.infer<typeof DraftClientMessage>;

// ── Server to client ──────────────────────────────────────────────────────

const Current = DraftView.shape.current;
const Upcoming = DraftView.shape.upcoming;
const Status = DraftView.shape.status;

function message<T extends string, P extends z.ZodType>(type: T, payload: P) {
  return z.object({ type: z.literal(type), seq: z.number().int(), at: Iso, payload });
}

export const DraftServerMessage = z.discriminatedUnion('type', [
  message('snapshot', z.object({ view: DraftView })),
  message('draft.room', z.object({ roomOpen: z.boolean(), scheduledAt: Iso.nullable() })),
  message('draft.started', z.object({ status: Status, current: Current })),
  message(
    'draft.picked',
    z.object({
      cell: DraftBoardCell,
      removedPlayerId: z.string(),
      current: Current,
      upcoming: Upcoming,
      lastPick: DraftBoardCell.nullable(),
      mine: DraftMine,
    }),
  ),
  message('draft.turn', z.object({ current: Current, upcoming: Upcoming })),
  message('draft.paused', z.object({ status: Status, current: Current })),
  message('draft.resumed', z.object({ status: Status, current: Current })),
  message(
    'draft.undone',
    z.object({
      cell: DraftBoardCell,
      restoredPlayer: DraftPlayerRow,
      current: Current,
      upcoming: Upcoming,
      lastPick: DraftBoardCell.nullable(),
      mine: DraftMine,
    }),
  ),
  message('draft.clock', z.object({ pickSeconds: z.number().int(), current: Current })),
  message('draft.seat', z.object({ seat: DraftSeat })),
  message('draft.queue', z.object({ mine: DraftMine })),
  message('draft.completed', z.object({ status: Status, current: z.null() })),
  /**
   * A trade happened, or the commissioner undid one: the board cells it
   * moved, whose turn it is (a traded pick on the clock keeps its time), my
   * half, and the trades.
   */
  message(
    'draft.traded',
    z.object({
      cells: z.array(DraftBoardCell),
      current: Current,
      upcoming: Upcoming,
      lastPick: DraftBoardCell.nullable(),
      mine: DraftMine,
      trading: DraftTrading,
    }),
  ),
  message(
    'draft.trade.undone',
    z.object({
      cells: z.array(DraftBoardCell),
      current: Current,
      upcoming: Upcoming,
      lastPick: DraftBoardCell.nullable(),
      mine: DraftMine,
      trading: DraftTrading,
    }),
  ),
  /**
   * My offers changed: one made to me or by me, answered, withdrawn, or made
   * untrue by the board. Sent to the two teams of each alone, and not
   * numbered, like a queue.
   */
  message('draft.offers', z.object({ trading: DraftTrading, closed: z.array(DraftOfferClosed) })),
  /**
   * An auction's block changed: a player put up, a bid, whose nomination it
   * is, the clocks paused or back, a sale undone. My own auto-bid alone, when
   * it moved nothing, comes to me unnumbered, like a queue.
   */
  message('draft.block', z.object({ auction: DraftAuction })),
  /** A player sold: his square on the board, the last sale, my half, and the block after it. */
  message(
    'draft.sold',
    z.object({
      cell: DraftBoardCell,
      removedPlayerId: z.string(),
      lastPick: DraftBoardCell.nullable(),
      mine: DraftMine,
      auction: DraftAuction,
    }),
  ),
  message(
    'intent.rejected',
    z.object({
      intentId: z.string(),
      code: ErrorCode,
      message: z.string(),
      details: ApiErrorDetails.optional(),
    }),
  ),
  message('pong', z.object({ sentAt: z.number() })),
]);
export type DraftServerMessage = z.infer<typeof DraftServerMessage>;
